import { useMutation, useQuery, useTRPC } from "@luckymenu/api-client/react";
import type { Bill } from "@luckymenu/billing-core";
import { useCallback, useState } from "react";
import { type BillingError, billingProblemOf } from "../billing/problems";
import { useBillCache } from "./use-bill";
import { useCan } from "./use-navigation";

/** A bill by its ID, for its page in More > Bills (P4-T20). Kept fresh by billing events. */
export function useBill(billId: string) {
	const trpc = useTRPC();
	const allowed = useCan("bill.create");
	const query = useQuery({
		...trpc.staff.billing.getBill.queryOptions({ billId }),
		enabled: allowed,
	});
	const problem = query.isError ? billingProblemOf(query.error) : null;
	return {
		status: query.data
			? ("ready" as const)
			: problem?.problem === "notFound"
				? ("notFound" as const)
				: problem
					? ("error" as const)
					: ("loading" as const),
		bill: query.data ?? null,
		retry: () => void query.refetch(),
	};
}

export type BillAction = "void" | "reverse";

/**
 * Voiding a bill (OP-15) and reversing a payment recorded by mistake, each
 * with a reason; owners and managers (`bill.void`, `canVoid`). A bill with
 * payments is voided only after they are reversed (`billHasPayments`); a
 * void bill keeps its number and its order is open again, to change and
 * bill anew. Each returns the bill, or null with `problem` set.
 */
export function useBillActions() {
	const trpc = useTRPC();
	const cache = useBillCache();
	const canVoid = useCan("bill.void");
	const [problem, setProblem] = useState<(BillingError & { action: BillAction }) | null>(null);
	const voidBill = useMutation(trpc.staff.billing.void.mutationOptions({ onSuccess: cache }));
	const reverse = useMutation(
		trpc.staff.billing.reversePayment.mutationOptions({ onSuccess: cache }),
	);
	const run = useCallback(async (action: BillAction, call: () => Promise<Bill>) => {
		setProblem(null);
		try {
			return await call();
		} catch (error) {
			setProblem({ ...billingProblemOf(error), action });
			return null;
		}
	}, []);
	const voidMutate = voidBill.mutateAsync;
	const reverseMutate = reverse.mutateAsync;
	return {
		canVoid,
		voidBill: useCallback(
			(billId: string, reason: string) => run("void", () => voidMutate({ billId, reason })),
			[run, voidMutate],
		),
		reversePayment: useCallback(
			(paymentId: string, reason: string) =>
				run("reverse", () => reverseMutate({ paymentId, reason })),
			[run, reverseMutate],
		),
		busy: voidBill.isPending ? ("void" as const) : reverse.isPending ? ("reverse" as const) : null,
		problem,
		clearProblem: useCallback(() => setProblem(null), []),
	};
}
